/**
 * Integrytas Group - Common Security Module (CSM)
 * useNotifications — unified notification + toast engine.
 *
 * Responsibilities:
 *   • Load persisted notifications from csm_notification on mount.
 *   • Subscribe to Supabase Realtime so server-pushed notifications (from
 *     Edge Functions, triggers, or other users) appear instantly as toasts.
 *   • Provide notify() for client-side code to fire a toast AND persist a
 *     DB record in one call.
 *   • Manage the ephemeral toast queue (auto-dismiss timers).
 *   • Expose markRead() / markAllRead() for the notification bell.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiDb } from './api_client';
import { supabase } from './supabase_client';
import { subscribeRealtime } from './realtime';
import type { AuthSession } from '../types/auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NotificationType = 'SUCCESS' | 'INFO' | 'WARNING' | 'ERROR' | 'SYSTEM';

export interface Notification {
  notification_id: number;
  user_id:         number;
  notification_type: NotificationType;
  title:           string;
  body:            string | null;
  is_read:         boolean;
  source_page:     string | null;
  created_at:      string;
}

export interface Toast {
  toast_id:          string;   // client-only ephemeral id
  notification_type: NotificationType;
  title:             string;
  body?:             string;
}

export interface NotificationCtx {
  notifications:  Notification[];
  unreadCount:    number;
  toasts:         Toast[];
  notify:         (type: NotificationType, title: string, body?: string) => Promise<void>;
  markRead:       (id: number) => Promise<void>;
  markAllRead:    () => Promise<void>;
  dismissToast:   (toastId: string) => void;
}

export const NotificationContext = createContext<NotificationCtx>({
  notifications: [],
  unreadCount:   0,
  toasts:        [],
  notify:        async () => { /* no-op default; replaced by the provider */ },
  markRead:      async () => { /* no-op default; replaced by the provider */ },
  markAllRead:   async () => { /* no-op default; replaced by the provider */ },
  dismissToast:  () => { /* no-op default; replaced by the provider */ },
});

// Auto-dismiss delays (ms). Error & Warning require manual dismiss.
const AUTO_DISMISS: Partial<Record<NotificationType, number>> = {
  SUCCESS: 4000,
  INFO:    5000,
};

const MAX_HISTORY = 50;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useNotificationState(session: AuthSession): NotificationCtx {
  const userId = session.effective_user.user_id;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [toasts,        setToasts]        = useState<Toast[]>([]);
  const timerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Load notification history from DB.
  useEffect(() => {
    void apiDb
      .from('csm_notification')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(MAX_HISTORY)
      .then(({ data }) => setNotifications((data ?? []) as Notification[]));
  }, [userId]);

  // Supabase Realtime subscription — receives server-pushed notifications.
  useEffect(() => {
    return subscribeRealtime(() => supabase
      .channel(`csm_notif_${userId}`)
      .on(
        'postgres_changes' as Parameters<ReturnType<typeof supabase.channel>['on']>[0],
        {
          event:  'INSERT',
          schema: 'public',
          table:  'csm_notification',
          filter: `user_id=eq.${userId}`,
        },
        (payload: { new: Notification }) => {
          const row = payload.new as Notification;
          setNotifications(prev => [row, ...prev].slice(0, MAX_HISTORY));
          addToast({
            toast_id:          `rt_${row.notification_id}`,
            notification_type: row.notification_type,
            title:             row.title,
            body:              row.body ?? undefined,
          });
        }
      ));

    // addToast is a stable useCallback ([] deps); the subscription must only be
    // (re)created when userId changes, not when addToast's identity does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Cleanup timers on unmount.
  useEffect(() => {
    const timers = timerRef.current;
    return () => { timers.forEach(clearTimeout); };
  }, []);

  const addToast = useCallback((t: Toast) => {
    setToasts(prev => [...prev.slice(-4), t]); // keep max 5 visible
    const delay = AUTO_DISMISS[t.notification_type];
    if (delay) {
      const timer = setTimeout(() => dismissToast(t.toast_id), delay);
      timerRef.current.set(t.toast_id, timer);
    }
    // dismissToast is a stable useCallback ([] deps) defined below; including it
    // would not change behaviour and creates a needless ordering coupling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissToast = useCallback((toastId: string) => {
    clearTimeout(timerRef.current.get(toastId));
    timerRef.current.delete(toastId);
    setToasts(prev => prev.filter(t => t.toast_id !== toastId));
  }, []);

  const notify = useCallback(async (
    type:  NotificationType,
    title: string,
    body?: string,
  ) => {
    // Optimistic toast — show immediately.
    const toastId = `local_${Date.now()}`;
    addToast({ toast_id: toastId, notification_type: type, title, body });

    // Persist to DB (also triggers Realtime, but we deduplicate by toast_id prefix).
    const { data } = await apiDb.rpc('csm_push_notification', {
      p_user_id:            userId,
      p_notification_type:  type,
      p_title:              title,
      p_body:               body ?? null,
    });

    // Sync the returned row into notifications list.
    if (data) {
      setNotifications(prev =>
        [data as Notification, ...prev].slice(0, MAX_HISTORY)
      );
    }
  }, [userId, addToast]);

  const markRead = useCallback(async (id: number) => {
    setNotifications(prev =>
      prev.map(n => n.notification_id === id ? { ...n, is_read: true } : n)
    );
    await apiDb
      .from('csm_notification')
      .update({ is_read: true })
      .eq('notification_id', id);
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    await apiDb
      .from('csm_notification')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);
  }, [userId]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return { notifications, unreadCount, toasts, notify, markRead, markAllRead, dismissToast };
}

export function useNotifications(): NotificationCtx {
  return useContext(NotificationContext);
}
